import { backOff } from 'exponential-backoff';

import logger from '../../lib/logger';
import {
  createPartUploadUrl,
  uploadChunkToS3,
  uploadChunkToFallback,
  initializeMultipartUpload,
  finalizeUpload as finalizeMultipartUpload,
} from '../uploadService';
import { ContentType } from '../../constants/mediaFormatProfiles';
import { TimeStampedLine } from '../../types';
import trackRecorderError from './errorTracking';

export interface UploadParams {
  teamId: string;
  folderId: string;
  fileId: string;
  uploadId: string;
  contentType: ContentType;
}

export interface UploadStateParams {
  teamId: string | null;
  folderId: string | null;
  fileId: string | null;
  uploadId: string | null;
  contentType: ContentType | null;
}

export interface ChunkUploadParams extends UploadParams {
  blob: Blob;
  partNumber: number;
}

/**
 * Retry utility function that can be used for any async operation
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options: {
    /** A human-readable name used in logs */
    operationName: string;
    /** Max attempts (default 10). Alias: maxRetries */
    numOfAttempts?: number;
    maxRetries?: number;
    /** Initial delay before the 2nd attempt in ms (default 1000). Alias: retryDelay */
    startingDelay?: number;
    retryDelay?: number;
    /** Maximum delay between attempts in ms (default 30000) */
    maxDelay?: number;
    /** Exponential factor (default 2) */
    timeMultiple?: number;
    /** Whether to apply jitter (default full) */
    jitter?: 'full' | 'none';
    /** Whether to delay the very first attempt (default false) */
    delayFirstAttempt?: boolean;
  },
): Promise<T> {
  const {
    operationName,
    numOfAttempts,
    maxRetries,
    startingDelay,
    retryDelay,
    maxDelay,
    timeMultiple,
    jitter,
    delayFirstAttempt,
  } = options;

  // Respect NODE_ENV === 'test' to keep delays tiny during unit tests
  const isTest = process.env.NODE_ENV === 'test';

  const attempts = numOfAttempts ?? maxRetries ?? 10;
  const rawStartingDelay = startingDelay ?? retryDelay ?? (isTest ? 10 : 1000);
  const initialDelay = isTest
    ? Math.min(rawStartingDelay, 10)
    : rawStartingDelay;
  const rawMaxDelay = maxDelay ?? 30000;
  const maximumDelay = isTest ? Math.min(rawMaxDelay, 100) : rawMaxDelay;
  const factor = timeMultiple ?? 2;
  const jitterMode = jitter ?? 'full';

  let currentAttempt = 0;

  const wrappedOperation = async (): Promise<T> => {
    currentAttempt += 1;
    logger.info(`${operationName} (attempt ${currentAttempt}/${attempts})`);
    return operation();
  };

  try {
    const result = await backOff(wrappedOperation, {
      startingDelay: initialDelay,
      maxDelay: maximumDelay,
      jitter: jitterMode,
      numOfAttempts: attempts,
      timeMultiple: factor,
      delayFirstAttempt: !!delayFirstAttempt,
      retry: (error: any) => {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        logger.warn(
          `${operationName} attempt ${currentAttempt} failed:`,
          errorMessage,
        );
        // Keep retrying until attempts exhausted
        return true;
      },
    });
    logger.info(`${operationName} succeeded on attempt ${currentAttempt}`);
    return result;
  } catch (lastError: any) {
    logger.error(`${operationName} failed after ${attempts} attempts`);
    throw lastError;
  }
}

/**
 * Pure function to upload a single chunk
 * This function is easily testable as it has no side effects beyond the upload itself
 * Tries S3 upload first, then falls back to direct backend upload if S3 fails
 */
export async function uploadSingleChunk(
  params: ChunkUploadParams,
): Promise<void> {
  const { teamId, folderId, fileId, uploadId, contentType, blob, partNumber } =
    params;

  try {
    // Step 1: Try the normal S3 upload flow
    logger.info(
      `Attempting S3 upload for part ${partNumber}, fileId: ${fileId}, uploadId: ${uploadId}`,
    );

    // Get upload URL with retry logic
    const url = await withRetry(
      async () => {
        const uploadUrl = await createPartUploadUrl({
          teamId,
          folderId,
          uploadId,
          fileId,
          partNumber,
          contentType,
        });

        if (!uploadUrl) {
          throw new Error('No upload URL provided');
        }

        return uploadUrl;
      },
      {
        maxRetries: 10,
        retryDelay: 1000,
        operationName: `Creating upload URL for part ${partNumber}`,
      },
    );

    logger.info(`Successfully created upload URL for part ${partNumber}`);

    // Upload to S3 with retry logic
    await withRetry(() => uploadChunkToS3({ uploadUrl: url, chunk: blob }), {
      maxRetries: 10,
      retryDelay: 1000,
      operationName: `Uploading part ${partNumber} to S3 (size: ${blob.size} bytes)`,
    });

    logger.info(`Successfully uploaded part ${partNumber} to S3`);
  } catch (s3Error) {
    // Step 2: If S3 upload completely fails, try the backend fallback
    logger.warn(
      `S3 upload failed for part ${partNumber} after all retries, attempting fallback upload:`,
      s3Error,
    );

    try {
      await withRetry(
        () =>
          uploadChunkToFallback({
            teamId,
            folderId,
            fileId,
            uploadId,
            partNumber,
            chunk: blob,
          }),
        {
          maxRetries: 5,
          retryDelay: 1000,
          operationName: `Fallback upload for part ${partNumber} (size: ${blob.size} bytes)`,
        },
      );

      logger.info(`Successfully uploaded part ${partNumber} using fallback`);
    } catch (fallbackError) {
      // Both S3 and fallback failed
      logger.error(
        `Both S3 and fallback uploads failed for part ${partNumber}:`,
        { s3Error, fallbackError },
      );
      throw new Error(
        `Upload failed: S3 error: ${
          s3Error instanceof Error ? s3Error.message : String(s3Error)
        }, Fallback error: ${
          fallbackError instanceof Error
            ? fallbackError.message
            : String(fallbackError)
        }`,
      );
    }
  }
}

/**
 * Validates upload parameters and returns validated non-null parameters
 */
export function validateUploadParams(params: UploadStateParams): UploadParams {
  const { teamId, folderId, fileId, uploadId, contentType } = params;

  if (!teamId || !folderId || !fileId || !uploadId || !contentType) {
    throw new Error(
      'Missing required parameters (teamId, folderId, fileId, uploadId, contentType). Cannot upload chunks.',
    );
  }

  return { teamId, folderId, fileId, uploadId, contentType };
}

const debounceTime = 2000;
let debounceStartedAt: number | null = null;
let debounceResult: any | null = null;

/**
 * Initialize multipart upload with debounce logic
 */
export async function initializeUploadWithDebounce(params: {
  teamId: string;
  folderId: string;
  contentType: ContentType;
}): Promise<{ fileId: string; uploadId: string }> {
  const { teamId, folderId, contentType } = params;

  logger.info(
    `Initializing multipart upload for team ${teamId}, folder ${folderId}`,
  );

  if (debounceStartedAt && Date.now() - debounceStartedAt < debounceTime) {
    logger.info(`Debouncing multipart upload for ${debounceTime}ms`);

    trackRecorderError({
      operation: 'initialization',
      errorType: 'InitializationError',
      errorMessage: 'Debouncing multipart upload',
      errorStack: '',
      fileId: debounceResult?.fileId || '',
    });

    return debounceResult!;
  }

  debounceResult = await initializeMultipartUpload({
    teamId,
    folderId,
    contentType,
  });

  debounceStartedAt = Date.now();

  return debounceResult;
}

/**
 * Finalize multipart upload with retry logic
 */
export async function finalizeUploadWithRetry(params: {
  teamId: string;
  folderId: string;
  fileId: string;
  uploadId: string;
  contentType: ContentType;
  notes: TimeStampedLine[];
  title?: string;
  needsConversion?: boolean;
}): Promise<any> {
  const {
    teamId,
    folderId,
    fileId,
    uploadId,
    contentType,
    notes,
    title,
    needsConversion,
  } = params;

  logger.info(
    `Finalizing multipart upload for file ${fileId}, upload ${uploadId}`,
  );

  try {
    return withRetry(
      () =>
        finalizeMultipartUpload({
          teamId,
          folderId,
          fileId,
          uploadId,
          contentType,
          notes,
          title,
          needsConversion,
        }),
      {
        maxRetries: 10,
        retryDelay: 1000,
        operationName: `Finalizing multipart upload for file ${fileId}`,
      },
    );
  } catch (error) {
    logger.error(`Failed to finalize upload with retry: ${error}`);
    throw error;
  }
}
